import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Tooltip from '../Tooltip';
import { TranslationsContext } from '../../context/TranslationContext';
import ChipGroup from './ChipGroup';
import {
    CHIP_ACTIVATION_KEYS,
    CHIP_SIZE,
    CHIP_VARIANT,
    CHIP_VARIANT_PROP_VALUES,
    LEGACY_CHIP_VARIANT,
} from './constants';
import { getSizeClass, getVariantClass } from './helpers';

const Chip = ({
    value,
    onRemove,
    variant,
    disabled,
    onClick,
    removeTooltip,
    removeLabel,
    showLoader = false,
    size,
    noBorder = false,
    v2 = false,
    className,
    ...rest
}) => {
    // Chip renders on legacy pages that mount no TranslationsProvider.
    const t = useContext(TranslationsContext)?.t ?? ((key) => key);
    const clickable = typeof onClick === 'function';

    const handleKeyDown = (e) => {
        if (e.target !== e.currentTarget || !CHIP_ACTIVATION_KEYS.includes(e.key)) return;
        e.preventDefault();
        onClick(e);
    };

    const renderRemove = () => {
        if (typeof onRemove !== 'function') return null;

        // Dismissing must not bubble into the chip's own onClick (e.g. a
        // toggle-select) — one click means remove, not remove + re-select.
        const handleRemove = (e) => {
            e.stopPropagation();
            onRemove(e);
        };

        const accessibleLabel = removeLabel
            || removeTooltip
            || (typeof value === 'string' && value ? `${t('Remove')} ${value}` : t('Remove'));

        const content = (
            <button
                type="button"
                className="chip-action icon-svg-x-close"
                aria-label={accessibleLabel}
                onClick={handleRemove}
            ></button>
        );

        if (!removeTooltip) return content;

        return <Tooltip title={removeTooltip}>
            {content}
        </Tooltip>;
    };

    return (
        <div
            className={classNames(
                'chip',
                getVariantClass(variant),
                getSizeClass(size),
                { disabled: disabled, 'no-border': noBorder, 'chip--new': v2 },
                className,
            )}
            onClick={clickable ? onClick : undefined}
            {...(clickable && {
                role: 'button',
                tabIndex: disabled ? -1 : 0,
                'aria-disabled': disabled || undefined,
                onKeyDown: handleKeyDown,
            })}
            {...rest}
        >
            {showLoader && <span className="chip-loader" aria-hidden />}
            {value}
            {renderRemove()}
        </div>
    );
};

Chip.propTypes = {
    /** Chip label — text, or a node when the label carries its own markup */
    value: PropTypes.node,
    /** Color variant — Design System name (`gray`, `brand`, `error`, …) or legacy name (`secondary`, `lightblue`, …) */
    variant: PropTypes.oneOf(CHIP_VARIANT_PROP_VALUES),
    /** Height preset */
    size: PropTypes.oneOf(Object.values(CHIP_SIZE)),
    /** Renders the dismiss "×"; called on click */
    onRemove: PropTypes.func,
    /** Hover tooltip on the dismiss "×" */
    removeTooltip: PropTypes.string,
    /** Translated accessible name of the dismiss "×"; defaults to the tooltip, else "Remove <value>" */
    removeLabel: PropTypes.string,
    /** Click handler for the whole chip; makes it a keyboard-operable button (Enter/Space) */
    onClick: PropTypes.func,
    /** Grays the chip out and shows a not-allowed cursor */
    disabled: PropTypes.bool,
    /** Shows a leading spinner */
    showLoader: PropTypes.bool,
    /** Drops the border */
    noBorder: PropTypes.bool,
    /** Legacy borderless restyle used by JobFooter save states */
    v2: PropTypes.bool,
    /** Extra class on the chip element */
    className: PropTypes.string,
};

export { ChipGroup, CHIP_VARIANT, LEGACY_CHIP_VARIANT, CHIP_SIZE };
export default Chip;
